import {
  differenceInCalendarDays,
  format,
  formatDistanceToNow,
  isToday,
  isTomorrow,
  isYesterday,
  parseISO,
  type Locale,
} from 'date-fns';
import { de, enUS, hi, id, ms, ta, th, vi, zhCN } from 'date-fns/locale';

// Single import surface for date logic: components pull date-fns primitives from
// here rather than 'date-fns' directly. Use `formatDate` (below) for user-facing,
// locale-aware display; `format` here is the raw formatter for machine strings.
export {
  addDays,
  differenceInCalendarDays,
  differenceInHours,
  format,
  isSameDay,
  isToday,
  isTomorrow,
  isYesterday,
  parseISO,
  subDays,
} from 'date-fns';

export type DateInput = Date | string | number;

/**
 * Common date format patterns (date-fns localized tokens — day/month/year order
 * and separators adapt per locale).
 */
export const DateFormats = {
  LONG_DATE: 'PPP', // September 4th, 1986
  SHORT_DATE: 'P', // 09/04/1986
  LONG_DATE_TIME: 'PPP p', // September 4th, 1986 8:30 PM
  FULL_DATE_TIME: 'PPPP p', // Thursday, September 4th, 1986 8:30 PM
  TIME: 'p', // 8:30 PM
  TIME_WITH_SECONDS: 'pp', // 8:30:00 PM
  MONTH_DAY: 'MMM d', // Sep 4
  YEAR_MONTH: 'yyyy MMMM', // 1986 September
} as const;

/**
 * Map i18n language codes to date-fns locales. Languages without a date-fns
 * locale (Tagalog, Burmese) fall back to English, matching the prior behaviour.
 */
const LOCALE_MAP: Record<string, Locale> = {
  en: enUS,
  hi,
  ta,
  tl: enUS, // Tagalog — no date-fns locale
  id,
  zh: zhCN,
  ms,
  th,
  vi,
  my: enUS, // Burmese — no date-fns locale
  de,
};

// date-fns has no global locale, so we hold the active one in module scope and
// pass it to every call — mirroring how dayjs.locale() worked previously.
let currentLocale: Locale = enUS;

/** Set the active date-fns locale from an i18n language code. */
export function setDateLocale(languageCode: string): void {
  currentLocale = LOCALE_MAP[languageCode] ?? enUS;
}

/** The currently active date-fns locale (for callers that format() directly). */
export function getDateLocale(): Locale {
  return currentLocale;
}

function toDate(value: DateInput): Date {
  if (value instanceof Date) return value;
  if (typeof value === 'number') return new Date(value);
  return parseISO(value);
}

/**
 * Format a date with the active locale, using date-fns format tokens
 * (e.g. 'PPP', 'yyyy-MM-dd', 'EEE d MMM').
 */
export function formatDate(date: DateInput, formatStr: string = DateFormats.LONG_DATE): string {
  return format(toDate(date), formatStr, { locale: currentLocale });
}

/** Relative time, e.g. "about 3 hours ago", "in 2 days". */
export function getRelativeTime(date: DateInput): string {
  return formatDistanceToNow(toDate(date), { addSuffix: true, locale: currentLocale });
}

/** Calendar-style time, e.g. "Today at 2:30 PM", "Yesterday at 10:00 AM". */
export function getCalendarTime(date: DateInput): string {
  const d = toDate(date);
  const time = format(d, 'p', { locale: currentLocale });

  if (isToday(d)) return `Today at ${time}`;
  if (isYesterday(d)) return `Yesterday at ${time}`;
  if (isTomorrow(d)) return `Tomorrow at ${time}`;

  const diff = differenceInCalendarDays(d, new Date());
  if (diff > 0 && diff < 7) return `${format(d, 'EEEE', { locale: currentLocale })} at ${time}`;
  if (diff < 0 && diff > -7)
    return `Last ${format(d, 'EEEE', { locale: currentLocale })} at ${time}`;

  return format(d, 'P', { locale: currentLocale });
}
